import type { PixelsSummary } from '../../services/pixelsService';

/**
 * Home-card billing state. Subscription is checked first: the backend marks
 * the trial over when the user subscribes, so a paid subscription always
 * wins over any transient trial flag.
 */
export type BillingCardState = 'pro' | 'trial' | 'none';

export interface BillingCardCta {
  label: string;
  /** Frontend route — checkout lives ONLY on /billing. */
  to: '/billing';
  variant: 'primary' | 'secondary';
}

export interface BillingCardModel {
  state: BillingCardState;
  line1: string;
  line2: string;
  /** Muted helper note rendered under the CTAs, if any. */
  note: string | null;
  ctas: BillingCardCta[];
}

const formatRenewalDate = (iso: string | null | undefined): string | null => {
  if (typeof iso !== 'string' || iso === '') return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

/**
 * Pure mapping from a parsed pixels summary to the home billing card model.
 * Dependency-free (type-only imports) so it loads in the node:test sandbox
 * with no stubs. Exactly one status line is ever produced.
 */
export const resolveBillingStatus = (
  summary: PixelsSummary | null
): BillingCardModel => {
  const available = summary?.available ?? null;
  // Plain text only — the coin artwork is rendered by <PixlesCoin /> next
  // to this string so the icon is consistent on every device.
  const line1 =
    typeof available === 'number' && Number.isFinite(available)
      ? `${available} pixles`
      : 'Pixles unavailable';

  const subscriptionActive = summary?.subscription?.active === true;
  if (subscriptionActive) {
    const renewal = formatRenewalDate(summary?.subscription?.currentPeriodEnd);
    const rawPlan = summary?.subscription?.plan;
    const planName =
      typeof rawPlan === 'string' && rawPlan !== ''
        ? rawPlan.charAt(0).toUpperCase() + rawPlan.slice(1)
        : 'Pro';
    const line2 =
      `${planName} · renews ${renewal ?? '—'}` +
      (summary?.subscription?.cancelAtPeriodEnd === true
        ? ' · cancelling'
        : '');
    return {
      state: 'pro',
      line1,
      line2,
      note: null,
      ctas: [
        { label: 'Buy Pixles', to: '/billing', variant: 'primary' },
        { label: 'Manage', to: '/billing', variant: 'secondary' },
      ],
    };
  }

  if (summary?.trial?.active === true) {
    const days = summary.trial.daysRemaining;
    const line2 =
      typeof days === 'number' && Number.isFinite(days)
        ? `Trial · ${days}d left`
        : 'Trial · active';
    return {
      state: 'trial',
      line1,
      line2,
      // Trial users can only subscribe to Pro — top-ups stay locked.
      note: 'Top-ups unlock after trial',
      ctas: [{ label: 'Subscribe', to: '/billing', variant: 'primary' }],
    };
  }

  return {
    state: 'none',
    line1,
    line2: 'No active plan',
    note: null,
    ctas: [
      { label: 'Subscribe', to: '/billing', variant: 'primary' },
      { label: 'Buy Pixles', to: '/billing', variant: 'secondary' },
    ],
  };
};
